import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, getErrorMessage } from '../lib/api';
import { toast, alert as sweetAlert } from '../utils/toast';
import {
  LoadingState,
  EmptyState,
  Breadcrumbs,
  BreadcrumbItem,
  Pagination,
} from '../components/ui';
import type { Product } from '../types';
import {
  Package,
  Plus,
  Search,
  X,
  Settings,
  Globe,
  EyeOff,
  Calendar,
  Key,
  FileText,
  Code,
} from 'lucide-react';

export const ProductManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    openapi_spec: '',
    auth_type: 'api_key',
    requires_external_auth: false,
    external_auth_type: 'Basic Auth',
    external_auth_config: '',
  });
  const [activeTab, setActiveTab] = useState<'basic' | 'auth' | 'docs'>('basic');

  // Search, Filter & Pagination states
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft'>('all');
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);

  // Close modals on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      const response = await api.get<{ data: Product[] }>('/admin/products');
      return response.data.data;
    },
  });

  const filteredProducts = React.useMemo(() => {
    if (!products) return [];
    return products.filter((p) => {
      const query = search.toLowerCase().trim();
      const matchesSearch =
        !query ||
        p.name.toLowerCase().includes(query) ||
        (p.description && p.description.toLowerCase().includes(query));

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'published' && p.published) ||
        (statusFilter === 'draft' && !p.published);

      return matchesSearch && matchesStatus;
    });
  }, [products, search, statusFilter]);

  const totalPublished = React.useMemo(() => {
    return products?.filter((p) => p.published).length ?? 0;
  }, [products]);

  const totalDraft = React.useMemo(() => {
    return products?.filter((p) => !p.published).length ?? 0;
  }, [products]);

  const paginatedProducts = React.useMemo(() => {
    return filteredProducts.slice(offset, offset + limit);
  }, [filteredProducts, offset, limit]);

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        ...data,
        external_auth_config: data.external_auth_config
          ? JSON.parse(data.external_auth_config)
          : undefined,
      };
      const response = await api.post('/admin/products', payload);
      const created = response.data?.data || response.data;

      // Auto-create endpoints if an OpenAPI spec was provided during creation
      if (created?.id && data.openapi_spec?.trim()) {
        try {
          const raw = data.openapi_spec.trim();
          let parsed: any = null;
          if (raw.startsWith('{')) {
            parsed = JSON.parse(raw);
          } else {
            const yaml = await import('js-yaml');
            parsed = yaml.load(raw);
          }
          const paths = parsed?.paths || {};
          const methods = ['get', 'post', 'put', 'patch', 'delete'];
          const endpointsToCreate: Array<Record<string, any>> = [];
          Object.entries(paths).forEach(([path, pathObj]: [string, any]) => {
            methods.forEach((m) => {
              if (pathObj?.[m]) {
                endpointsToCreate.push({
                  name:
                    pathObj[m].summary || pathObj[m].operationId || `${m.toUpperCase()} ${path}`,
                  description: pathObj[m].description || '',
                  public_method: m.toUpperCase(),
                  public_path: path,
                  external_method: m.toUpperCase(),
                  external_path: path,
                  enabled: true,
                });
              }
            });
          });
          if (endpointsToCreate.length > 0) {
            await api.post(`/portal/admin/products/${created.id}/endpoints/bulk`, {
              endpoints: endpointsToCreate,
            });
          }
        } catch {
          // Non-fatal if spec auto-import fails during creation
        }
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        openapi_spec: '',
        auth_type: 'api_key',
        requires_external_auth: false,
        external_auth_type: 'Basic Auth',
        external_auth_config: '',
      });
      setActiveTab('basic');
      toast.success('Produto criado com sucesso!');
    },
    onError: (err) => {
      toast.error(`Erro ao criar o produto: ${getErrorMessage(err)}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData & { id: string }) => {
      const payload = {
        ...data,
        external_auth_config: data.external_auth_config
          ? JSON.parse(data.external_auth_config)
          : null,
      };
      const response = await api.put(`/admin/products/${data.id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        openapi_spec: '',
        auth_type: 'api_key',
        requires_external_auth: false,
        external_auth_type: 'Basic Auth',
        external_auth_config: '',
      });
      setActiveTab('basic');
      toast.success('Produto atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar o produto.');
    },
  });

  const publishMutation = useMutation({
    mutationFn: async (data: { id: string; published: boolean }) => {
      await api.put(`/admin/products/${data.id}/publish`, { published: data.published });
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success(
        variables.published ? 'Produto publicado com sucesso!' : 'Produto despublicado com sucesso!'
      );
    },
    onError: () => {
      toast.error('Erro ao alterar estado de publicação.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      updateMutation.mutate({ id: editingProduct.id, ...formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTogglePublish = async (product: Product) => {
    const actionText = product.published ? 'despublicar' : 'publicar';
    const result = await sweetAlert.confirm(
      `${product.published ? 'Despublicar' : 'Publicar'} Produto?`,
      `Tem a certeza de que deseja ${actionText} o produto "${product.name}"?`
    );
    if (result.isConfirmed) {
      publishMutation.mutate({ id: product.id, published: !product.published });
    }
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      openapi_spec: '',
      auth_type: 'api_key',
      requires_external_auth: false,
      external_auth_type: 'Basic Auth',
      external_auth_config: '',
    });
    setActiveTab('basic');
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Breadcrumbs>
          <BreadcrumbItem href="/">Início</BreadcrumbItem>
          <BreadcrumbItem>Produtos</BreadcrumbItem>
        </Breadcrumbs>
        <LoadingState type="card" count={3} />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Breadcrumbs>
        <BreadcrumbItem href="/">Início</BreadcrumbItem>
        <BreadcrumbItem>Produtos</BreadcrumbItem>
      </Breadcrumbs>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-xs">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-xl bg-primary-50 dark:bg-primary-950/60 border border-primary-200 dark:border-primary-800 flex items-center justify-center text-primary-600 dark:text-primary-400 shrink-0">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">
              Gestão do Catálogo de Produtos
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Gira os produtos de API, especificações OpenAPI, planos de tarifação e rotas
              sincronizadas no Kong Gateway.
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setEditingProduct(null);
            setFormData({
              name: '',
              description: '',
              openapi_spec: '',
              auth_type: 'api_key',
              requires_external_auth: false,
              external_auth_type: 'Basic Auth',
              external_auth_config: '',
            });
            setActiveTab('basic');
            setShowCreateModal(true);
          }}
          className="btn-base btn-md btn-primary self-start sm:self-auto shrink-0 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          <span>Novo Produto</span>
        </button>
      </div>

      {/* KPI Summary Cards */}
      {products && products.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Total no Catálogo
              </p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                {products.length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Package className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Publicados (Portal Dev)
              </p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                {totalPublished}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
              <Globe className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Em Rascunho
              </p>
              <p className="text-2xl font-bold text-slate-700 dark:text-slate-300 mt-1">
                {totalDraft}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-200/80 dark:border-slate-800 shadow-xs flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                Endpoints Mapeados
              </p>
              <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400 mt-1">
                {products.reduce((acc, p) => acc + Number(p.endpoint_count || 0), 0)}
              </p>
            </div>
            <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <Code className="w-5 h-5" />
            </div>
          </div>
        </div>
      )}

      {/* Filter and Search Bar */}
      {products && products.length > 0 && (
        <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* Search Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              placeholder="Pesquisar por nome ou descrição..."
              className="w-full pl-10 pr-9 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700/80 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setOffset(0);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                title="Limpar pesquisa"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Segmented Status Tabs */}
          <div className="inline-flex p-1 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-semibold overflow-x-auto">
            <button
              type="button"
              onClick={() => {
                setStatusFilter('all');
                setOffset(0);
              }}
              className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all flex items-center gap-1.5 ${
                statusFilter === 'all'
                  ? 'bg-white dark:bg-slate-700 text-primary-600 dark:text-primary-400 shadow-2xs font-bold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span>Todos</span>
              <span className="px-1.5 py-0.2 rounded-full text-2xs bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                {products.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setStatusFilter('published');
                setOffset(0);
              }}
              className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all flex items-center gap-1.5 ${
                statusFilter === 'published'
                  ? 'bg-white dark:bg-slate-700 text-emerald-600 dark:text-emerald-400 shadow-2xs font-bold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span>Publicados</span>
              <span className="px-1.5 py-0.2 rounded-full text-2xs bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300">
                {totalPublished}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setStatusFilter('draft');
                setOffset(0);
              }}
              className={`px-3 py-1.5 rounded-lg whitespace-nowrap transition-all flex items-center gap-1.5 ${
                statusFilter === 'draft'
                  ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 shadow-2xs font-bold'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              <span>Rascunhos</span>
              <span className="px-1.5 py-0.2 rounded-full text-2xs bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
                {totalDraft}
              </span>
            </button>
          </div>
        </div>
      )}

      {products && products.length > 0 ? (
        filteredProducts.length > 0 ? (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-6 flex flex-col justify-between hover:shadow-lg hover:-translate-y-0.5 transition-all group"
                >
                  <div>
                    <div className="flex justify-between items-start gap-2 mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3
                            className="text-lg font-bold text-slate-900 dark:text-white line-clamp-1 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors"
                            title={product.name}
                          >
                            {product.name}
                          </h3>
                          {product.version && (
                            <span className="text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 shrink-0">
                              v{product.version}
                            </span>
                          )}
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold shrink-0 border ${
                          product.published
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-800/50'
                            : 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700'
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            product.published ? 'bg-emerald-500' : 'bg-slate-400'
                          }`}
                        />
                        {product.published ? 'Publicado' : 'Rascunho'}
                      </span>
                    </div>

                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 line-clamp-3 min-h-[3.75rem] leading-relaxed">
                      {product.description}
                    </p>

                    <div className="flex flex-wrap items-center gap-1.5 mb-4">
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-700/70 text-slate-700 dark:text-slate-300">
                        <Code className="w-3.5 h-3.5 text-indigo-500" />
                        <span>{Number(product.endpoint_count || 0)} endpoints</span>
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono text-xs px-2.5 py-1 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200/70 dark:border-slate-700/70 text-slate-700 dark:text-slate-300">
                        <Key className="w-3.5 h-3.5 text-primary-500" />
                        <span>{product.authentication_provider || 'API_KEY'}</span>
                      </span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800/80">
                    <div className="text-xs text-slate-500 dark:text-slate-400 mb-3.5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5 text-slate-400" />
                        <span>
                          Criado em {new Date(product.created_at).toLocaleDateString('pt-PT')}
                        </span>
                      </span>
                      <span
                        className={`text-[11px] font-medium ${
                          product.external_url_production || product.external_url_sandbox
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-amber-600 dark:text-amber-400'
                        }`}
                      >
                        {product.external_url_production || product.external_url_sandbox
                          ? 'Upstream configurado'
                          : 'Sem URL upstream'}
                      </span>
                    </div>

                    <div className="flex items-center gap-2.5">
                      <Link
                        to={`/products/${product.id}`}
                        className="btn-base btn-sm btn-primary flex-1 flex items-center justify-center gap-1.5"
                      >
                        <Settings className="w-3.5 h-3.5" />
                        <span>Configurar</span>
                      </Link>

                      <button
                        type="button"
                        onClick={() => handleTogglePublish(product)}
                        disabled={publishMutation.isPending}
                        className={`btn-base btn-sm flex items-center justify-center gap-1.5 border transition-all ${
                          product.published
                            ? 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/60'
                            : 'border-emerald-600 text-white bg-emerald-600 hover:bg-emerald-700 shadow-xs'
                        }`}
                      >
                        {product.published ? (
                          <>
                            <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                            <span>Despublicar</span>
                          </>
                        ) : (
                          <>
                            <Globe className="w-3.5 h-3.5" />
                            <span>Publicar</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            <Pagination
              total={filteredProducts.length}
              limit={limit}
              offset={offset}
              onPageChange={(newOffset) => setOffset(newOffset)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setOffset(0);
              }}
              limitOptions={[6, 9, 12, 24]}
              className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800"
            />
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-8 text-center">
            <EmptyState
              title="Nenhum produto encontrado"
              description="Nenhum produto corresponde aos filtros ou pesquisa selecionados."
              action={
                <button
                  type="button"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('all');
                    setOffset(0);
                  }}
                  className="btn-base btn-md btn-secondary"
                >
                  Limpar Filtros
                </button>
              }
            />
          </div>
        )
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200/80 dark:border-slate-800 p-8 text-center">
          <EmptyState
            title="Nenhum produto criado"
            description="Comece por criar o seu primeiro produto de API para publicar no marketplace."
            action={
              <button
                type="button"
                onClick={() => setShowCreateModal(true)}
                className="btn-base btn-md btn-primary flex items-center gap-2 mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Criar Primeiro Produto</span>
              </button>
            }
          />
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center z-50 p-4 animate-fade-in"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModal();
            }
          }}
        >
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-slate-200 dark:border-slate-800 animate-scale-in">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2.5">
                <Package className="w-6 h-6 text-primary-500" />
                <span>{editingProduct ? 'Editar Produto' : 'Criar Novo Produto de API'}</span>
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                title="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {(createMutation.isError || updateMutation.isError) && (
                <div className="rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 p-4">
                  <p className="text-sm text-rose-800 dark:text-rose-300">
                    {getErrorMessage(createMutation.error || updateMutation.error)}
                  </p>
                </div>
              )}

              {/* Tabs */}
              <div className="border-b border-slate-200 dark:border-slate-800">
                <nav className="-mb-px flex space-x-6" aria-label="Tabs">
                  <button
                    type="button"
                    onClick={() => setActiveTab('basic')}
                    className={`py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                      activeTab === 'basic'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    <FileText className="w-4 h-4" />
                    <span>Básico</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('auth')}
                    className={`py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                      activeTab === 'auth'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    <Key className="w-4 h-4" />
                    <span>Autenticação</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab('docs')}
                    className={`py-3 px-1 border-b-2 font-semibold text-sm flex items-center gap-1.5 transition-colors ${
                      activeTab === 'docs'
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    <Code className="w-4 h-4" />
                    <span>Documentação</span>
                  </button>
                </nav>
              </div>

              {activeTab === 'basic' && (
                <div className="space-y-4 animate-fade-in">
                  <div>
                    <label htmlFor="name" className="label-modern">
                      Nome do Produto *
                    </label>
                    <input
                      id="name"
                      type="text"
                      required
                      className="input-modern"
                      placeholder="Ex: Pagamentos Instantâneos API"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label htmlFor="description" className="label-modern">
                      Descrição *
                    </label>
                    <textarea
                      id="description"
                      required
                      className="input-modern h-32"
                      placeholder="Descreva as funcionalidades e objetivos desta API..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                </div>
              )}

              {activeTab === 'auth' && (
                <div className="space-y-4 animate-fade-in">
                  <div>
                    <label htmlFor="auth_type" className="label-modern">
                      Autenticação Interna (Gateway)
                    </label>
                    <select
                      id="auth_type"
                      className="input-modern"
                      value={formData.auth_type}
                      onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
                    >
                      <option value="api_key">API Key (Padrão)</option>
                      <option value="oauth2">OAuth 2.0</option>
                      <option value="jwt">JWT</option>
                    </select>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Método utilizado pelos clientes para autenticar as chamadas no Gateway.
                    </p>
                  </div>

                  <div className="pt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        id="requires_external"
                        type="checkbox"
                        className="w-4 h-4 text-primary-600 focus:ring-primary-500 border-slate-300 dark:border-slate-700 rounded"
                        checked={formData.requires_external_auth}
                        onChange={(e) =>
                          setFormData({ ...formData, requires_external_auth: e.target.checked })
                        }
                      />
                      <span className="text-sm font-semibold text-slate-900 dark:text-white">
                        Requer Credenciais Externas (Upstream)
                      </span>
                    </label>
                  </div>

                  {formData.requires_external_auth && (
                    <div className="pl-4 space-y-4 border-l-2 border-primary-500/40 mt-3">
                      <div>
                        <label htmlFor="external_auth_type" className="label-modern">
                          Tipo de Autenticação Externa
                        </label>
                        <select
                          id="external_auth_type"
                          className="input-modern"
                          value={formData.external_auth_type}
                          onChange={(e) =>
                            setFormData({ ...formData, external_auth_type: e.target.value })
                          }
                        >
                          <option value="Basic Auth">Basic Auth</option>
                          <option value="Bearer Token">Bearer Token</option>
                          <option value="API Key">API Key</option>
                          <option value="OAuth 2.0">OAuth 2.0</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="external_auth_config" className="label-modern">
                          Configuração (JSON)
                        </label>
                        <textarea
                          id="external_auth_config"
                          className="input-modern h-24 font-mono text-xs"
                          placeholder='{"fields": ["username", "password"]}'
                          value={formData.external_auth_config}
                          onChange={(e) =>
                            setFormData({ ...formData, external_auth_config: e.target.value })
                          }
                        />
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                          Esquema JSON que define os campos que o desenvolvedor terá de preencher no
                          portal.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'docs' && (
                <div className="space-y-4 animate-fade-in">
                  <div>
                    <label htmlFor="openapi_spec" className="label-modern">
                      OpenAPI / Swagger Spec
                    </label>
                    <textarea
                      id="openapi_spec"
                      className="input-modern h-48 font-mono text-xs"
                      placeholder="openapi: 3.0.0..."
                      value={formData.openapi_spec}
                      onChange={(e) => setFormData({ ...formData, openapi_spec: e.target.value })}
                    />
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      Cole a especificação OpenAPI (JSON ou YAML) para renderizar a documentação
                      interativa.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-base btn-md btn-secondary flex-1"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="btn-base btn-md btn-primary flex-1 disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? 'A guardar...'
                    : editingProduct
                      ? 'Atualizar Produto'
                      : 'Criar Produto'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
